import _ from 'lodash';
//services
import API from 'services/api.services';
//constants
import {
	API_BENEFICIARY_WC,
	API_LOAD_BENEFICIARIES,
	API_GET_BENEFICIARY_PAYMENTS,
	API_VALIDATE_BENEFICIARY_BANK_ACCOUNT,
	API_BENEFICIARY_CREATION,
	API_DELETE_BENEFICIARY,
	API_EDIT_BENEFICIARY_NOTE,
	API_SIGN_BENEFICIARY,
	API_REJECT_BENEFICIARY,
	API_GET_PAYMENT_CATEGORIES,
} from 'constants/db.constants';
import {
	Beneficiary,
	beneficiarySteps,
	beneficiaryStepsStatus,
	BeneficiaryDetailsCompany,
	ValidateBankAccount,
	beneficiaryType,
	BeneficiaryDetailsLocal,
	BeneficiaryStatus,
	paymentsMethodsMapping,
	PaymentCategoryOption,
} from 'constants/beneficiary.constants';
import { intialMessage } from 'constants/ui.constants';
//reducers
import {
	setActiveStep,
	setLoading,
	setMessage,
	setSteps,
	setBeneficiaries,
	upsertBeneficiary,
	setOptionalPayments,
	setLoadingBeneficiaries,
	setStartLoadingBeneficiaries,
	setBeneficiaryStatus,
} from 'state/reducers/beneficiary.reducers';
import { store } from 'state/store';
import { UserPermission } from 'constants/user.constants';
import { setAwaitingAuthorizationCategories, setOpenPendingCategories } from 'state/reducers/requests.reducers';
import { PendingRequestTypes } from 'constants/requests.constants';
import { setErrorFieldsMessage, setErrorMessage } from 'utils';
import { CCUniqueNames, CCUniqueNamesMessage } from 'constants/global.constants';

export function validateStep1(benfeciary: Beneficiary, isNew: boolean, agreeDuplicate: boolean) {
	//TODO: change name validateStep1 to more clear
	let paramsSetting = {
		companyId: store.getState().user.details?.companyId,
	};
	if (_.isEqual(benfeciary.type, beneficiaryType.COMPANY)) {
		BeneficiaryDetailsCompany.forEach(
			(key) => (paramsSetting[key] = benfeciary[key] ? String(benfeciary[key]) : '')
		);
	} else {
		BeneficiaryDetailsLocal.forEach((key) => (paramsSetting[key] = benfeciary[key] ? String(benfeciary[key]) : ''));
	}
	if (benfeciary.id) {
		paramsSetting['id'] = benfeciary.id;
	}
	paramsSetting['isNew'] = isNew;
	paramsSetting['agreeDuplicate'] = agreeDuplicate;
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setMessage(intialMessage));
		await API.getInstance()
			.post(API_BENEFICIARY_WC, paramsSetting)
			.then((response) => {
				dispatch(setLoading(false));
				try {
					const { id, backupBeneficiaryId } = response.data.newBeneficiary;
					dispatch(
						upsertBeneficiary({
							...benfeciary,
							id,
							backupBeneficiaryId,
						})
					);
					dispatch(
						setSteps({
							[beneficiarySteps.BENEFICIARY]: {
								status: beneficiaryStepsStatus.inProgress,
							},
						})
					);
					dispatch(setActiveStep(beneficiarySteps.BANK_ACCOUNT));
				} catch (error) {
					console.log('step1:', error);
				}
			})
			.catch((error) => {
				dispatch(setLoading(false));
				setErrorMessage(setMessage, error);
			});
	};
}

export function getPaymentsOptions(benfeciary: Beneficiary, reset: boolean, isNew: boolean = true) {
	//TODO: change name validateStep1 to more clear
	let paramsSetting = {
		currencyId: benfeciary.currencyId,
		bankAccountCountryId: benfeciary.bankAccountCountryId,
		beneficiaryCountryId: benfeciary.countryId,
		beneficiaryId: benfeciary.id,
		isNew,
	};
	return async (dispatch: any) => {
		dispatch(setMessage(intialMessage));
		await API.getInstance()
			.post(API_GET_BENEFICIARY_PAYMENTS, paramsSetting)
			.then((response) => {
				try {
					const res = response.data.allowedPaymentMethods;
					if (reset) {
						dispatch(
							upsertBeneficiary({
								payments: res,
								bicSwift: '',
								acctNumber: '',
								iban: '',
								aba: '',
							})
						);
					} else {
						dispatch(
							upsertBeneficiary({
								payments: res,
							})
						);
					}
					dispatch(setOptionalPayments());
				} catch (error) {
					console.log('step1:', error);
				}
			})
			.catch((error) => {
				setErrorMessage(setMessage, error);
			});
	};
}

export const flattenPaymentCategories = (categories: any[]): PaymentCategoryOption[] => {
	const result: PaymentCategoryOption[] = [];

	const walk = (node: any, trail: string[] = []) => {
		// Use the backend name directly as the translation key
		const translationKey = node?.name;
		const nextTrail = [...trail, translationKey];

		if (node?.children && node.children.length) {
			node.children
				.sort((a, b) => (a?.sort_order || 0) - (b?.sort_order || 0))
				.forEach((child: any) => walk(child, nextTrail));
		} else {
			const [primary, secondary, tertiary] = nextTrail;
			result.push({
				id: String(node?.id),
				label: nextTrail.filter(Boolean).join(' / '),
				primary,
				secondary,
				tertiary,
			});
		}
	};

	(categories || []).sort((a, b) => (a?.sort_order || 0) - (b?.sort_order || 0)).forEach((root) => walk(root, []));

	return result;
};

/**
 * Converts allowedPaymentCategories tree structure to flat array of PaymentCategoryOption objects
 * Used when beneficiaries are loaded from the API
 */
export const transformBeneficiaryCategories = (beneficiary: any): PaymentCategoryOption[] => {
	const allowedPaymentCategories = beneficiary?.allowedPaymentCategories;

	if (
		!allowedPaymentCategories ||
		!Array.isArray(allowedPaymentCategories) ||
		allowedPaymentCategories.length === 0
	) {
		return [];
	}

	return flattenPaymentCategories(allowedPaymentCategories);
};

export async function getPaymentCategories(): Promise<PaymentCategoryOption[]> {
	const companyId = store.getState().user.details?.companyId;
	if (!companyId) {
		console.error('Company ID not available');
		return [];
	}

	try {
		const response = await API.getInstance().get(API_GET_PAYMENT_CATEGORIES, { companyId });
		const apiCategories = response?.data?.categories;
		if (apiCategories && Array.isArray(apiCategories)) {
			return flattenPaymentCategories(apiCategories);
		}
	} catch (e) {
		console.error('Failed to fetch payment categories from API:', e);
	}
	return [];
}

export function buildCategoryGroups(
	paymentCategories: (string | PaymentCategoryOption)[] | undefined,
	categoryMap: Record<string, PaymentCategoryOption>
): { primOrder: string[]; groups: Map<string, { secondaryOrder: string[]; items: Map<string, string[]> }> } {
	const primOrder: string[] = [];
	const groups = new Map<string, { secondaryOrder: string[]; items: Map<string, string[]> }>();

	for (const item of paymentCategories || []) {
		const isString = typeof item === 'string';
		const categoryId = isString ? item : item.id;
		const option = isString ? categoryMap[item] : item;

		if (!option) continue;

		const primary = option.primary || option.label || categoryId;
		const secondaryRaw = option.secondary?.trim();
		const secondaryKey = secondaryRaw && secondaryRaw !== primary ? secondaryRaw : primary;
		const tertiaryItems =
			option.tertiary
				?.split(',')
				.map((item) => item.trim())
				.filter(Boolean) || [];

		if (!groups.has(primary)) {
			groups.set(primary, { secondaryOrder: [], items: new Map() });
			primOrder.push(primary);
		}

		const bucket = groups.get(primary)!;
		if (!bucket.items.has(secondaryKey)) {
			bucket.items.set(secondaryKey, []);
			bucket.secondaryOrder.push(secondaryKey);
		}

		const addItem = (text: string) => {
			if (!text) return;
			if (text === primary || text === secondaryKey) return;
			const arr = bucket.items.get(secondaryKey)!;
			if (!arr.includes(text)) arr.push(text);
		};

		// Only add tertiary items if they exist
		if (tertiaryItems.length) {
			tertiaryItems.forEach(addItem);
		} else if (secondaryKey === primary) {
			// If secondary is the same as primary (no real hierarchy), add the label as tertiary
			addItem(option.label || categoryId);
		}
		// If secondary is different from primary and no tertiary exists,
		// this is a secondary-level leaf node - don't add any tertiary items
	}

	return { primOrder, groups };
}

export function validateStep2(benfeciary: ValidateBankAccount) {
	//TODO: change name validateStep1 to more clear

	const paramsSetting = {
		...benfeciary,
		isSigned: [UserPermission.OPERATIONER, UserPermission.SIGNER].includes(store.getState().user.userPermission),
		companyId: store.getState().user.details?.companyId,
	};

	return async (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setMessage(intialMessage));
		let objError = {};
		await API.getInstance()
			.post(API_VALIDATE_BENEFICIARY_BANK_ACCOUNT, paramsSetting)
			.then((response) => {
				dispatch(setLoading(false));
				if (response.data.success) {
					dispatch(setActiveStep(beneficiarySteps.REVIEW));
				}
			})
			.catch((error) => {
				dispatch(setLoading(false));
				try {
					_.keys(error.data?.errors).forEach((key) => {
						if (key === 'base') {
							objError['forms.beneficiary.field.' + key] = _.get(error.data?.errors, key);
						} else {
							objError['forms.beneficiary.field.' + key] = 'error.code.invalid_format';
						}
					});
					_.keys(error.data?.error).forEach((key) => {
						if (key === 'base') {
							objError['forms.beneficiary.field.' + key] = _.get(error.data?.error, key);
						} else {
							if (_.keys(paymentsMethodsMapping).includes(key)) {
								objError[`page.beneficiary.create.step.2.payment_method.${key}.text`] =
									'validations.errors.not_match';
							} else if (_.keys(CCUniqueNames).includes(key)) {
								objError[CCUniqueNames[key]] = _.keys(CCUniqueNamesMessage).includes(key)
									? CCUniqueNamesMessage[key]
									: 'validations.errors.not_match';
							} else {
								objError['forms.beneficiary.field.' + key] = 'validations.errors.not_match';
							}
						}
					});
				} catch (e) {
					console.log(e);
				}
				if (error.data?.errors || error.data?.error) {
					if (_.keys(objError).length > 0) {
						setErrorFieldsMessage(setMessage, error, objError);
					} else {
						setErrorMessage(setMessage, error);
					}
				}
			});
	};
}

export function createBeneficiary(benfeciary: ValidateBankAccount, isNew?: boolean) {
	const paramsSetting = {
		...benfeciary,
		companyId: store.getState().user.details?.companyId,
		isSigned: true,
	};
	/*if (_.includes([UserPermission.SIGNER, UserPermission.OPERATIONER], 
    store.getState().user.userPermission)) {
		_.assign(paramsSetting, { isSigned: true });
	}*/

	return async (dispatch: any, getState: any) => {
		dispatch(setLoading(true));
		dispatch(setMessage(intialMessage));
		await API.getInstance()
			.post(API_BENEFICIARY_CREATION, paramsSetting)
			.then((response) => {
				dispatch(setLoading(false));
				try {
					if (response.data.success) {
						const { beneficiary } = getState();
						dispatch(upsertBeneficiary(response.data.beneficiary));
						if (isNew) {
							dispatch(setBeneficiaries(_.concat(beneficiary.items, [response.data.beneficiary])));
						} else {
							dispatch(
								setBeneficiaryStatus({
									beneficiary: response.data.beneficiary,
								})
							);
						}
						dispatch(setActiveStep(beneficiarySteps.COMPLETE));
					}
				} catch (e) {
					console.log(e);
					dispatch(setActiveStep(beneficiarySteps.COMPLETE));
				}
			})
			.catch((error) => {
				setTimeout(() => {
					dispatch(setLoading(false));
					//dispatch(setActiveStep(beneficiarySteps.COMPLETE));
					setErrorMessage(setMessage, error);
				}, 2000);
			});
	};
}

export function loadBeneficiaries() {
	//TODO: change name validateStep1 to more clear
	let paramsSetting = {
		companyId: store.getState().user.details?.companyId,
		status: (Object.keys(BeneficiaryStatus) as (keyof typeof BeneficiaryStatus)[]).filter((key, index) => {
			return (
				BeneficiaryStatus[key] !== BeneficiaryStatus.None &&
				BeneficiaryStatus[key] !== BeneficiaryStatus.PendingBankAccountDetails
			);
		}),
	};
	return async (dispatch: any, getState: any) => {
		dispatch(setLoadingBeneficiaries(true));
		await API.getInstance()
			.post(API_LOAD_BENEFICIARIES, paramsSetting)
			.then((response) => {
				setTimeout(() => {
					dispatch(setLoadingBeneficiaries(false));
				}, 500);
				dispatch(setStartLoadingBeneficiaries(true));
				try {
					if (response.data.success) {
						const beneficiaries = response.data.data;
						dispatch(setBeneficiaries(beneficiaries));
					}
				} catch (error) {}
			})
			.catch((error) => {
				setErrorMessage(setMessage, error);
				dispatch(setStartLoadingBeneficiaries(true));
				dispatch(setLoadingBeneficiaries(false));
			});
	};
}

export function deleteBeneficiary(beneficiaryId: number, isNew: boolean = false) {
	//TODO: change name validateStep1 to more clear
	let paramsSetting = {
		id: beneficiaryId,
		isNew,
	};
	return async (dispatch: any, getState: any) => {
		await API.getInstance()
			.post(API_DELETE_BENEFICIARY, paramsSetting)
			.then((response) => {
				try {
					if (response.data.success) {
						dispatch(loadBeneficiaries());
					}
				} catch (error) {}
			})
			.catch((error) => {
				setErrorMessage(setMessage, error);
			});
	};
}

export function updateBeneficiaryNote(beneficiaryId: number, note: string) {
	//TODO: change name validateStep1 to more clear
	let paramsSetting = {
		beneficiaryId: beneficiaryId,
		note: note,
	};

	return async (dispatch: any, getState: any) => {
		dispatch(setLoading(true));
		await API.getInstance()
			.post(API_EDIT_BENEFICIARY_NOTE, paramsSetting)
			.then((response) => {
				dispatch(setLoading(false));
				try {
					if (response.data.success) {
						dispatch(upsertBeneficiary({ note: note }));
					}
				} catch (error) {}
			})
			.catch((error) => {
				dispatch(setLoading(false));
				setErrorMessage(setMessage, error);
			});
	};
}

export function changeBeneficiaryStatus(beneficiaryId: number, status: BeneficiaryStatus) {
	//TODO: change name validateStep1 to more clear
	let paramsSetting = {
		id: beneficiaryId,
	};
	const api = status === BeneficiaryStatus.Approved ? API_SIGN_BENEFICIARY : API_REJECT_BENEFICIARY;
	return async (dispatch: any, getState: any) => {
		dispatch(setLoading(true));
		await API.getInstance()
			.post(api, paramsSetting)
			.then((response) => {
				let { pendingRequests } = getState();
				let _pendingRequests = { ...pendingRequests };
				dispatch(setLoading(false));
				try {
					let filter = _.filter(
						_pendingRequests.awaitingAuth[PendingRequestTypes.BENEFICIARIES],
						function (o) {
							return o.id !== beneficiaryId;
						}
					);
					dispatch(
						setAwaitingAuthorizationCategories({
							list: filter,
							category: PendingRequestTypes.BENEFICIARIES,
						})
					);
					if (status === BeneficiaryStatus.Approved) {
						let find = _.filter(
							_pendingRequests.awaitingAuth[PendingRequestTypes.BENEFICIARIES],
							function (o) {
								return o.id === beneficiaryId;
							}
						);
						dispatch(
							setOpenPendingCategories({
								list: _.concat(_pendingRequests.openPending[PendingRequestTypes.BENEFICIARIES], find),
								category: PendingRequestTypes.BENEFICIARIES,
							})
						);
					}
				} catch (error) {
					console.log(error);
				}
			})
			.catch((error) => {
				dispatch(setLoading(false));
				setErrorMessage(setMessage, error);
			});
	};
}
